import { Label } from '@/components/label';
import { styled } from '@/styles/stitches.config';
import { ComposableStyledComponentProps } from '@/types/composable-styled-component';
import { motion } from 'framer-motion';
import { FC } from 'react';
import usePrevious from '../../utils/use-previous';
import { BarChartItem } from './vertical';

type HorizontalBarChartProps = ComposableStyledComponentProps<HTMLElement> & {
  visible?: boolean;
  data?: BarChartItem[];
  label: string;
  formatValue?: (d: number) => string;
  hasAlternatingColors?: boolean;
  hasSeparateLabel?: boolean;
};

const Root = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  paddingInline: '$3',
  width: '100%',
  maxHeight: '100%',
  '@bp5': {
    paddingBlockEnd: '$3',
  },
});

const BarRoot = styled('div', {
  display: 'flex',
  alignItems: 'center',
});

const Item = styled('div', {
  minWidth: '100%',
  marginBlockEnd: '$3',
  '@bp5': {
    marginBlockEnd: '$5',
  },
});

const Bar = styled(motion.div, {
  borderRadius: 'calc($space$6 + $space$2)',
  height: 'calc($space$6 + $space$2)',
  backgroundColor: 'var(--color-primary)',
  '@bp2': {
    borderRadius: '$space$7',
    height: '$space$7',
  },
});

const Value = styled('span', {
  fontWeight: '$bold',
  fontSize: '$3',
  color: 'var(--color-text-light)',
  marginInlineStart: '$4',
  whiteSpace: 'nowrap',
});

export const HorizontalBarChart: FC<HorizontalBarChartProps> = (props) => {
  const {
    visible = true,
    data,
    label,
    css,
    formatValue = (d: number) => String(d),
    hasAlternatingColors,
    hasSeparateLabel = false,
  } = props;
  const prevVisible = usePrevious(visible);

  return (
    <Root>
      {!hasSeparateLabel && (
        <Label
          css={{
            color: 'var(--color-text-light)',
            fontWeight: '$medium',
            marginBlockEnd: '$5',
            maxWidth: '60ch',
            ...css,
          }}
        >
          {label}
        </Label>
      )}

      {data &&
        data.map((d, i) => (
          <Item key={d.label}>
            <motion.div animate={{ opacity: Number(visible) }} initial>
              <Label
                css={{
                  color: 'var(--color-text-light)',
                  marginBlockEnd: '$2',
                  fontSize: '$1',
                  '@bph1': {
                    fontSize: '$2',
                  },
                }}
              >
                {d.label}
              </Label>
            </motion.div>

            <BarRoot>
              <Bar
                css={{
                  background: hasAlternatingColors
                    ? ['var(--color-primary)', 'var(--color-secondary)', 'var(--color-tertiary)'][
                        i % 3
                      ]
                    : 'var(--color-primary)',
                }}
                key={d.label}
                initial={{ width: 0 }}
                animate={{
                  // note: this could theoretically push the label outside of the parent, causing overflow
                  // however, none of our data goes > 90%, so in practice, that doesn't happen
                  // we should change how the chart is rendered if it does.
                  width: `${d.value * Number(visible) * 100}%`,
                }}
                transition={{
                  width: {
                    delay: visible && !prevVisible ? i * 0.1 : 0,
                    type: 'spring',
                    damping: 15,
                    mass: 1,
                    stiffness: 100,
                  },
                }}
              />

              <Value>{formatValue(d.value)}</Value>
            </BarRoot>
          </Item>
        ))}
    </Root>
  );
};
